import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { type SheetFunctions } from '@vanguard/axs-components';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';
import { ClientTaxInfoComponent } from '../client-tax-info/client-tax-info.component';
import { RothConvDetailsComponent } from '../roth-conv-details/roth-conv-details.component';

@Component({
  selector: 'mobile-inputs-sheet',
  standalone: true,
  imports: [CommonModule, ClientTaxInfoComponent, RothConvDetailsComponent],
  templateUrl: './mobile-inputs-sheet.component.html',
  styleUrl: './mobile-inputs-sheet.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class MobileInputsSheetComponent implements OnDestroy {
  @ViewChild('mobileSheetRef') mobileSheetRef: any;
  @Output() infoIconClicked = new EventEmitter<{ accordion: string; fieldId: string }>();
  @Output() updateClicked = new EventEmitter<void>();

  content = content.Data.v4_rcc_root.content.mobileInputSheet;
  sampleClientsContent = content.Data.v4_rcc_root.content.sampleClientsSection;
  clientTaxTitle = content.Data.v4_rcc_root.content.clientInfoTax.title;
  rothConvTitle = content.Data.v4_rcc_root.content.rothConvDetails.rothConvHeader;
  appliedSampleClientTitle: string | null = null;
  hasEditedAppliedSampleClient = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly contentService: ContentService,
    private readonly sharedInfoService: SharedInfoService,
  ) {
    this.contentService
      .getContent()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.content = data.Data.v4_rcc_root.content.mobileInputSheet;
        this.sampleClientsContent = data.Data.v4_rcc_root.content.sampleClientsSection;
        this.clientTaxTitle = data.Data.v4_rcc_root.content.clientInfoTax?.title;
        this.rothConvTitle = data.Data.v4_rcc_root.content.rothConvDetails?.rothConvHeader;
      });

    this.sharedInfoService
      .getSampleClientIndex$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((index) => {
        this.appliedSampleClientTitle = this.getAppliedSampleClientTitle(index);
      });

    this.sharedInfoService
      .getSampleClientLoaded$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasEditedAppliedSampleClient = false;
      });

    this.sharedInfoService
      .getSampleClientModified$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((modified) => {
        this.hasEditedAppliedSampleClient = modified;
      });
  }

  get headerTitle(): string {
    return this.content?.header;
  }

  get description(): string {
    return this.content?.description;
  }

  get updateButtonText(): string {
    return this.content?.update;
  }

  get sampleClientBannerPrefix(): string {
    return this.sampleClientsContent?.sampleClientModals?.inputbannerprefix || '';
  }

  get showAppliedSampleClientBanner(): boolean {
    return Boolean(this.appliedSampleClientTitle) && !this.hasEditedAppliedSampleClient;
  }

  openSheet(): void {
    (this.mobileSheetRef?.nativeElement as unknown as SheetFunctions | undefined)?.show?.();
  }

  closeSheet(): void {
    (this.mobileSheetRef?.nativeElement as unknown as SheetFunctions | undefined)?.close?.();
  }

  onUpdate(): void {
    // Ensure focused inputs commit value/change before closing the sheet.
    (document.activeElement as HTMLElement | null)?.blur?.();
    this.updateClicked.emit();
    this.closeSheet();
  }

  onInfoIcon(event: { accordion: string; fieldId: string }): void {
    this.infoIconClicked.emit(event);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getAppliedSampleClientTitle(index: number): string | null {
    if (index <= 0) {
      return null;
    }

    const sampleClientTitle =
      this.sampleClientsContent?.sampleClientModals?.data?.[index - 1]?.title;
    return typeof sampleClientTitle === 'string' && sampleClientTitle.trim().length > 0
      ? sampleClientTitle
      : null;
  }
}
